import React, { useState } from 'react';
import axios from 'axios';
import SEO from './SEO';

interface IpInfo {
  ip: string;
  country: string;
  region: string;
  city: string;
  isp: string;
  org: string;
  timezone: string;
  lat: number;
  lon: number;
  query: string;
}

const IpLookup: React.FC = () => {
  const [ipAddress, setIpAddress] = useState('');
  const [ipInfo, setIpInfo] = useState<IpInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const validateIP = (ip: string): boolean => {
    const ipv4Regex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    const ipv6Regex = /^(?:[0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$/;
    return ipv4Regex.test(ip) || ipv6Regex.test(ip);
  };

  const lookupIP = async () => {
    if (!ipAddress.trim()) {
      setError('请输入IP地址');
      return;
    }

    if (!validateIP(ipAddress.trim())) {
      setError('请输入有效的IP地址');
      return;
    }

    setLoading(true);
    setError('');
    setIpInfo(null);

    try {
      // 使用免费的IP查询API
      const response = await axios.get(`http://ip-api.com/json/${ipAddress.trim()}?fields=status,message,country,regionName,city,isp,org,timezone,lat,lon,query`);
      
      if (response.data.status === 'success') {
        setIpInfo({
          ip: response.data.query,
          country: response.data.country,
          region: response.data.regionName,
          city: response.data.city,
          isp: response.data.isp,
          org: response.data.org,
          timezone: response.data.timezone,
          lat: response.data.lat,
          lon: response.data.lon,
          query: response.data.query
        });
      } else {
        setError(response.data.message || '查询失败');
      }
    } catch (err) {
      console.error('IP lookup error:', err);
      setError('查询失败，请检查网络连接或稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const getCurrentIP = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.get('http://ip-api.com/json/?fields=query');
      if (response.data.query) {
        setIpAddress(response.data.query);
      }
    } catch (err) {
      setError('获取当前IP失败');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      lookupIP();
    }
  };

  return (
    <div className="tool-container">
      <SEO 
        title="IP地址查询工具 - 在线IP地址定位查询"
        description="免费在线IP地址查询工具，快速查询IP地址的地理位置、ISP信息、时区等详细信息。支持IPv4地址查询，数据准确可靠。"
        keywords="IP查询,IP地址查询,IP定位,地理位置查询,ISP查询,在线IP工具"
      />
      <h1 className="tool-title">IP地址查询</h1>
      <p className="tool-description">
        查询IP地址的地理位置、ISP信息和其他详细信息
      </p>

      <div className="form-group">
        <label className="form-label">IP地址</label>
        <div style={{ display: 'flex', gap: '10px' }}>
          <input
            type="text"
            className="form-input"
            value={ipAddress}
            onChange={(e) => setIpAddress(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="请输入IP地址，例如：8.8.8.8"
            style={{ flex: 1 }}
          />
          <button 
            className="btn btn-secondary"
            onClick={getCurrentIP}
            disabled={loading}
          >
            获取我的IP
          </button>
        </div>
      </div>

      <div style={{ textAlign: 'center', marginBottom: '20px' }}>
        <button 
          className="btn btn-primary"
          onClick={lookupIP}
          disabled={loading || !ipAddress.trim()}
        >
          {loading ? '查询中...' : '查询IP信息'}
        </button>
      </div>

      {error && (
        <div style={{
          backgroundColor: '#f8d7da',
          color: '#721c24',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '20px',
          border: '1px solid #f5c6cb'
        }}>
          {error}
        </div>
      )}

      {ipInfo && (
        <div className="result-container">
          <h3 className="result-title">查询结果</h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '20px'
          }}>
            <div style={{
              backgroundColor: 'white',
              padding: '20px',
              borderRadius: '8px',
              border: '1px solid #e0e0e0'
            }}>
              <h4 style={{ color: '#2c3e50', marginBottom: '15px' }}>基本信息</h4>
              <div style={{ lineHeight: '1.8' }}>
                <div><strong>IP地址:</strong> {ipInfo.ip}</div>
                <div><strong>国家:</strong> {ipInfo.country}</div>
                <div><strong>地区:</strong> {ipInfo.region}</div>
                <div><strong>城市:</strong> {ipInfo.city}</div>
              </div>
            </div>

            <div style={{
              backgroundColor: 'white',
              padding: '20px',
              borderRadius: '8px',
              border: '1px solid #e0e0e0'
            }}>
              <h4 style={{ color: '#2c3e50', marginBottom: '15px' }}>网络信息</h4>
              <div style={{ lineHeight: '1.8' }}>
                <div><strong>ISP:</strong> {ipInfo.isp}</div>
                <div><strong>组织:</strong> {ipInfo.org}</div>
                <div><strong>时区:</strong> {ipInfo.timezone}</div>
              </div>
            </div>

            <div style={{
              backgroundColor: 'white',
              padding: '20px',
              borderRadius: '8px',
              border: '1px solid #e0e0e0'
            }}>
              <h4 style={{ color: '#2c3e50', marginBottom: '15px' }}>地理位置</h4>
              <div style={{ lineHeight: '1.8' }}>
                <div><strong>纬度:</strong> {ipInfo.lat}</div>
                <div><strong>经度:</strong> {ipInfo.lon}</div>
                <div style={{ marginTop: '15px' }}>
                  <a 
                    href={`https://www.google.com/maps?q=${ipInfo.lat},${ipInfo.lon}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                    style={{ fontSize: '14px', padding: '8px 16px' }}
                  >
                    在地图中查看
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div style={{ marginTop: '30px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
        <h3 style={{ color: '#2c3e50', marginBottom: '15px' }}>使用说明</h3>
        <ul style={{ color: '#7f8c8d', lineHeight: '1.6' }}>
          <li>支持IPv4和IPv6地址查询</li>
          <li>点击"获取我的IP"可以自动填入当前公网IP</li>
          <li>查询结果包括地理位置、ISP信息等</li>
          <li>可以直接在地图中查看IP的地理位置</li>
          <li>所有查询都是实时进行的</li>
        </ul>
        <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#fff3cd', borderRadius: '6px', fontSize: '14px' }}>
          <strong>注意：</strong> 由于浏览器安全策略，某些网络环境下可能无法正常查询，建议在HTTPS环境下使用。
        </div>
      </div>
    </div>
  );
};

export default IpLookup;